import IconMenu from "@mui/icons-material/DensityMediumOutlined";
import IconDoubleArrowRight from "@mui/icons-material/KeyboardDoubleArrowRight";
import { Box, IconButton } from "@mui/material";
import { PropsWithChildren, useEffect, useState } from "react";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { useIntercom } from "react-use-intercom";

import AppBackground from "../../common/AppBackground";
import useBasicData from "../../hooks/useBasicData";
import usePathPattern from "../../hooks/usePathPattern";
import AppNavSidebar from "../nav/AppNavSidebar";
import AppNavTopbar from "../nav/AppNavTopbar";
import AppWarningsBar from "../nav/AppWarningsBar";
import TeamNoticesWrapper from "../notices/TeamNoticesWrapper";

declare global {
  interface Window {
    Intercom: (command: string, ...params: any[]) => void;
  }
}

export default function Layout({ children = <Outlet /> }: PropsWithChildren) {
  const { user, workspace } = useBasicData();
  const { update } = useIntercom();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const pathPattern = usePathPattern();

  // update intercom metadata
  useEffect(() => {
    if (user) {
      const customAttributes: Record<string, any> = {};
      if (user.personalWorkspace) {
        customAttributes.personal_workspace_tier =
          user.personalWorkspace.planTier;
      }
      if (workspace) {
        customAttributes.last_workspace_id = workspace.id;
        customAttributes.last_workspace_name = workspace.name;
        customAttributes.last_workspace_tier = workspace.planTier;
      }
      update({
        email: user.email,
        name: user.name,
        userId: user.id,
        userHash: user.intercomHash,
        avatar: {
          type: "avatar",
          imageUrl: user.profilePicture,
        },
        customAttributes,
      });
    }
  }, [user, workspace, update]);

  useEffect(() => {
    if (searchParams.has("onboard")) {
      // https://app.intercom.com/a/apps/sxjrhmhs/outbound/checklists/34234875
      window.Intercom?.("startChecklist", 34234875);
      setSearchParams((params) => {
        params.delete("onboard");
        return params;
      });
    }
  }, [searchParams, setSearchParams]);

  const sidebarIsHidden =
    [
      /^invite$/gm,
      /^inactive$/gm,
      /^tos$/gm,
      /^onboard$/gm,
      /^new$/gm,
      /^unverified$/gm,
    ].filter((pattern) => pattern.test(pathPattern)).length > 0;

  const sidebarIsCollapsed =
    [
      /^settings.*/gm,
      /^explore*/gm,
      /^:workspace\/welcome$/gm,
      /^:workspace\/pricing$/gm,
      /^:workspace\/forge$/gm,
      /^:workspace\/assets\/new$/gm,
      /^:workspace\/assets\/:assetId$/gm,
      /^:workspace\/styles.*/gm,
      /^:workspace\/drive.*/gm,
      /^:workspace\/forge-session\/:forgeSessionId$/gm,
      /^files\/:fileId/gm,
    ].filter((pattern) => pattern.test(pathPattern)).length > 0;

  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isMouseWithinSidebar, setMouseWithinSidebar] = useState(false);

  const handleOpenMenu = () => {
    if (!sidebarIsCollapsed) return;
    setSidebarOpen(true);
  };

  const handleCloseMenu = () => {
    if (!sidebarIsCollapsed || isMouseWithinSidebar) return;
    setSidebarOpen(false);
  };

  const handleMouseEnterMenu = () => {
    setMouseWithinSidebar(true);
  };

  const handleMouseLeaveMenu = () => {
    setMouseWithinSidebar(false);

    if (!sidebarIsCollapsed) return;
    setSidebarOpen(false);
  };

  useEffect(() => {
    handleCloseMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <TeamNoticesWrapper>
      <div className="flex flex-col w-screen h-screen overflow-hidden bg-white dark:bg-layer-950">
        <AppWarningsBar />

        <div className="flex h-full grow" id="appMain">
          {!!user && (
            <>
              <AppNavTopbar>
                {!sidebarIsHidden && (
                  <MenuButton
                    open={isSidebarOpen}
                    fixed={!sidebarIsCollapsed}
                    handleMouseEnter={handleOpenMenu}
                  />
                )}
              </AppNavTopbar>
              {!sidebarIsHidden && (
                <AppNavSidebar
                  open={isSidebarOpen}
                  fixed={!sidebarIsCollapsed}
                  onMouseEnter={handleMouseEnterMenu}
                  onMouseLeave={handleMouseLeaveMenu}
                />
              )}
            </>
          )}

          <AppBackground />
          <div className="relative w-full h-full overflow-hidden text-layer-500">
            {children}
          </div>
        </div>
      </div>
    </TeamNoticesWrapper>
  );
}

type MenuButtonProps = {
  open: boolean;
  fixed: boolean;
  handleMouseEnter: () => void;
};

function MenuButton({ open, fixed, handleMouseEnter }: MenuButtonProps) {
  const buttonClasses_Inactive =
    "relative shadow-button bg-white hover:bg-layer-200 border-layer-300 text-layer-600 hover:text-layer-700 dark:bg-layer-800 dark:hover:bg-layer-700 dark:border-layer-700 dark:hover:border-layer-600 dark:text-layer-300 dark:hover:text-layer-200";
  const buttonClasses_Active =
    "bg-layer-200 border-layer-300 text-layer-900 dark:bg-layer-700 dark:border-layer-600 dark:text-layer-200";

  const buttonClasses_Unfixed = "opacity-100";
  const buttonClasses_Fixed = "opacity-0";

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      className={`w-9 h-9 -ml-2 -mt-1 flex items-center justify-center rounded-full border group pointer-events-auto transition-all duration-500 ease-in-out ${
        fixed ? buttonClasses_Fixed : buttonClasses_Unfixed
      } ${open ? buttonClasses_Active : buttonClasses_Inactive}`}
      data-cy="nav-sidebar-menu-button"
    >
      <IconButton
        className={`w-9 h-9 flex items-center justify-center bg-transparent m-0 p-0 group-hover:text-layer-900 dark:group-hover:text-layer-50 ${
          fixed ? "cursor-default" : "cursor-pointer"
        }`}
        size="large"
        edge="end"
        aria-haspopup="true"
        color="inherit"
      >
        {open ? (
          <IconDoubleArrowRight
            fontSize="medium"
            className="group-hover:text-layer-900 dark:group-hover:text-layer-50"
          />
        ) : (
          <IconMenu
            fontSize="small"
            className="group-hover:text-layer-900 dark:group-hover:text-layer-50"
          />
        )}
      </IconButton>
    </Box>
  );
}
